import { useEffect, useState } from "react";
import { PDFDocument } from "pdf-lib";
import toast from "react-hot-toast";
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { formatAmount, formatDateDDMMYYYY } from "../../../Utils";
import { TOAST_MESSAGES } from "../constants";

const arrayBufferToBase64 = (buffer) => {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
};

const ONES = [
  "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
  "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
  "Seventeen", "Eighteen", "Nineteen",
];
const TENS = [
  "", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety",
];

const twoDigitsToWords = (num) => {
  if (num < 20) return ONES[num];
  const tens = Math.floor(num / 10);
  const rest = num % 10;
  return TENS[tens] + (rest ? ` ${ONES[rest]}` : "");
};

const threeDigitsToWords = (num) => {
  const hundred = Math.floor(num / 100);
  const rest = num % 100;
  const hundredPart = hundred ? `${ONES[hundred]} Hundred` : "";
  const restPart = rest ? twoDigitsToWords(rest) : "";
  return [hundredPart, restPart].filter(Boolean).join(" ");
};

// Indian numbering system (Crore / Lakh / Thousand)
const numberToIndianWords = (amount) => {
  let num = Math.round(Number(amount) || 0);
  if (num === 0) return "Zero";

  const crore = Math.floor(num / 10000000);
  num %= 10000000;
  const lakh = Math.floor(num / 100000);
  num %= 100000;
  const thousand = Math.floor(num / 1000);
  num %= 1000;
  const hundred = num;

  const parts = [];
  if (crore) parts.push(`${threeDigitsToWords(crore)} Crore`);
  if (lakh) parts.push(`${twoDigitsToWords(lakh)} Lakh`);
  if (thousand) parts.push(`${twoDigitsToWords(thousand)} Thousand`);
  if (hundred) parts.push(threeDigitsToWords(hundred));

  return parts.join(" ");
};

const TEMPLATE_PATHS_BY_COMPANY = {
  TECHB_ENERGY: "/pdf/salary_slip_techb_energy.pdf",
  TECHB_SOFTWARES: "/pdf/salary_slip_techb_softwares.pdf",
};

const FIELD_POSITION_ADJUSTMENTS = {
  pay_month: { x: -25, y: -2.5 },
  net_pay: { x:-10, y: -2 },
  net_pay_words: { x:-10, y: -2 },
};

const adjustFieldPosition = (field, adjustment) => {
  if (!adjustment) return;

  field.acroField.getWidgets().forEach((widget) => {
    const rect = widget.getRectangle();
    widget.setRectangle({
      ...rect,
      x: rect.x + (adjustment.x || 0),
      y: rect.y + (adjustment.y || 0),
    });
  });
};

export const useSalarySlipPdf = () => {
  const [pdfTemplates, setPdfTemplates] = useState({});

  /* ---------------- Load PDF Templates (one per company) ---------------- */
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const entries = await Promise.all(
          Object.entries(TEMPLATE_PATHS_BY_COMPANY).map(async ([company, path]) => {
            const bytes = await fetch(path).then((res) => res.arrayBuffer());
            return [company, await PDFDocument.load(bytes)];
          }),
        );
        setPdfTemplates(Object.fromEntries(entries));
      } catch {
        toast.error(TOAST_MESSAGES.PDF_TEMPLATE_LOAD_FAILED);
      }
    };

    loadTemplates();
  }, []);

  /* ---------------- Field Mapping ---------------- */
  const getSalarySlipFields = (data) => {
    const otherAdjustments = Number(data?.other_adjustments) || 0;
    const otherBonus = (Number(data?.referral_bonus) || 0) + Math.max(otherAdjustments, 0);
    const otherDeductions = Math.abs(Math.min(otherAdjustments, 0));

    return [
      {
        name: "pay_month",
        value: data?.start_date
          ? new Date(data.start_date).toLocaleString("en-US", {
              month: "long",
              year: "numeric",
            })
          : "",
      },
      { name: "employee_code", value: data?.userDetails?.employee_code },
      {
        name: "employee_name",
        value: `${data?.userDetails?.first_name ?? ""} ${
          data?.userDetails?.last_name ?? ""
        }`,
      },
      { name: "designation", value: data?.userDetails?.designation },
      { name: "department", value: data?.userDetails?.department },
      {
        name: "date_of_joining",
        value: formatDateDDMMYYYY(data?.userDetails?.date_of_joining),
      },
      { name: "work_location", value: data?.userDetails?.work_location },
      {
        name: "date_of_birth",
        value: formatDateDDMMYYYY(data?.userDetails?.birth_date),
      },
      { name: "shift", value: data?.userDetails?.shift },
      { name: "bank_name", value: data?.userDetails?.bank_name },
      { name: "bank_account_no", value: data?.userDetails?.salary_account_number },
      { name: "pan", value: data?.userDetails?.pan_number },
      { name: "uan", value: data?.userDetails?.uan_number },
      { name: "esic_account_no", value: data?.userDetails?.esic_account_number },
      { name: "aadhar_no", value: data?.userDetails?.aadhar_number },

      { name: "month_days", value: data?.total_days },
      { name: "leaves_absent", value: data?.leaves_absent_days },
      { name: "worked_days", value: data?.worked_days },

      { name: "basic_salary", value: formatAmount(data?.basic_amount) },
      { name: "hra", value: formatAmount(data?.hra) },
      { name: "conveyance_allowance", value: formatAmount(data?.conveyance_allowance) },
      { name: "lta_allowance", value: formatAmount(data?.lta_allowance) },
      { name: "medical_allowance", value: formatAmount(data?.medical_allowance) },
      { name: "bonus", value: formatAmount(data?.bonus) },
      { name: "attire_allowance", value: formatAmount(data?.attire_allowance) },
      { name: "reimbursements", value: formatAmount(data?.reimbursements) },
      { name: "other_bonus", value: formatAmount(otherBonus) },
      { name: "total_earnings", value: formatAmount(data?.total_earnings) },

      { name: "employee_pf", value: formatAmount(data?.employee_pf) },
      { name: "employee_esic", value: formatAmount(data?.employee_esic) },
      { name: "professional_tax", value: formatAmount(data?.pt) },
      { name: "employee_mlwf", value: formatAmount(data?.employee_mlwf) },
      { name: "tds", value: formatAmount(data?.tds) },
      { name: "other_deductions", value: formatAmount(otherDeductions) },
      { name: "total_deductions", value: formatAmount(data?.total_deductions) },

      { name: "net_pay", value: formatAmount(data?.net) },
      {
        name: "net_pay_words",
        value: `${numberToIndianWords(data?.net)} Rupees Only`,
      },
    ];
  };

  /* ---------------- Download Logic ---------------- */
  const downloadSalarySlipPdf = async (data) => {
    try {
      const company = data?.userDetails?.company || "TECHB_ENERGY";
      const pdfTemplate = pdfTemplates[company];

      if (!pdfTemplate) {
        toast.error(TOAST_MESSAGES.PDF_TEMPLATE_LOADING);
        return;
      }

      const pdf = await PDFDocument.load(await pdfTemplate.save());
      const form = pdf.getForm();

      getSalarySlipFields(data).forEach(({ name, value }) => {
        try {
          const field = form.getTextField(name);
          adjustFieldPosition(field, FIELD_POSITION_ADJUSTMENTS[name]);
          field.setText(value != null ? String(value) : "");
        } catch {
        }
      });

      form.flatten();
      const pdfBytes = await pdf.save();

      const fileName = `SalarySlip_${data?.userDetails?.first_name}_${data?.userDetails?.last_name}.pdf`;

      if (Capacitor.getPlatform() === "web") {
        const blob = new Blob([pdfBytes], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        Object.assign(document.createElement("a"), {
          href: url,
          download: fileName,
        }).click();
        URL.revokeObjectURL(url);
      } else {
        await Filesystem.writeFile({
          path: fileName,
          data: arrayBufferToBase64(pdfBytes),
          directory: Directory.Documents,
          recursive: true,
        });
      }

      toast.success(TOAST_MESSAGES.PDF_DOWNLOAD_SUCCESS);
    } catch (err) {
      toast.error(TOAST_MESSAGES.PDF_DOWNLOAD_FAILED);
      console.error(err);
    }
  };

  return { downloadSalarySlipPdf };
};
